import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, signal, inject } from '@angular/core';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { MatButton } from '@angular/material/button';
import { MatToolbar } from '@angular/material/toolbar';
import { MatIcon } from '@angular/material/icon';
import { getCurrentUser, signOut } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { environment } from '../../environments/environment';

@Component({
  selector: 'app-main-layout',
  imports: [RouterOutlet, MatButton, MatToolbar, RouterLink, RouterLinkActive, MatIcon],
  template: `
    <mat-toolbar class="app-toolbar" role="navigation" aria-label="Primary">
      <a class="logo-link" mat-button routerLink="/" aria-label="Go to home">
        <img src="logo_white.png" alt="Tinesight" class="logo" />
      </a>
      <span class="wordmark">Tinesight</span>

      <span class="spacer" aria-hidden="true"></span>
      <a mat-button
         [href]="apiPath"
         target="_blank"
         aria-label="SDK"
      >SDK<mat-icon>open_in_new</mat-icon>
      </a>

      @if (!isAuthenticated()) {
        <a mat-button
           routerLink="/plans"
           routerLinkActive="active-link"
           [routerLinkActiveOptions]="{ exact: true }"
           aria-label="Plans"
        >Sign up</a>

        <a mat-button
           routerLink="/login"
           routerLinkActive="active-link"
           [routerLinkActiveOptions]="{ exact: true }"
           aria-label="Login"
        >Login</a>
      }

      @if (isAuthenticated()) {
        <a mat-button
           routerLink="/dashboard"
           routerLinkActive="active-link"
           [routerLinkActiveOptions]="{ exact: true }"
           aria-label="Dashboard"
        >Dashboard</a>

        <a mat-button
           routerLink="/subscriptions"
           routerLinkActive="active-link"
           [routerLinkActiveOptions]="{ exact: true }"
           aria-label="My Subscriptions"
        >My Subscriptions</a>

        <button mat-button
                (click)="handleSignOut()"
                aria-label="Sign out"
        >Sign out</button>
      }
    </mat-toolbar>
    <div class="content">
      <router-outlet />
    </div>
  `,
  styles: `
    :host {
      display: grid;
      min-height: 100dvh;
      grid-template-rows: auto 1fr;
    }

    .app-toolbar {
      padding-inline: 16px;
      display: flex;
      align-items: center;
      color: var(--color-ts-text);
      background: linear-gradient(90deg, #10101a 0%, #08080e 100%);
      border-bottom: 1px solid var(--color-ts-border);
    }

    .logo-link {
      display: inline-flex;
      align-items: center;
      text-decoration: none;

      .logo {
        height: 28px;
        display: block;
      }
    }

    .spacer {
      flex: 1 1 auto;
    }

    .wordmark {
      font-size: 1rem;
      font-weight: 700;
      letter-spacing: 0.14em;
      text-transform: uppercase;
    }

    a[mat-button],
    button[mat-button] {
      text-transform: none;
      font-weight: 500;
      color: var(--color-ts-muted);

      &.active-link {
        color: var(--color-ts-green);
        border-bottom: 2px solid var(--color-ts-green);
      }

      &:hover {
        color: var(--color-ts-text);
      }
    }

    // ponytail: no horizontal gutters — every page self-centers (max-width + margin:auto,
    // or flex), and the marketing page is full-bleed. Gutters here exposed the white body
    // behind the page's tinted background as side bars.
    .content {
      display: grid;

      router-outlet {
        display: none;
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MainLayoutComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  readonly apiPath = `https://${environment.SDK_SUBDOMAIN}.tinesight.com`;
  readonly isAuthenticated = signal(false);

  async ngOnInit() {
    await this.checkAuthState();

    const stopListening = Hub.listen('auth', async (data) => {
      if (data.payload.event === 'signedIn' || data.payload.event === 'signedOut') {
        await this.checkAuthState();
      }
    });
    this.destroyRef.onDestroy(stopListening);
  }

  private async checkAuthState() {
    try {
      await getCurrentUser();
      this.isAuthenticated.set(true);
    } catch {
      this.isAuthenticated.set(false);
    }
  }

  async handleSignOut() {
    try {
      await signOut();
      this.isAuthenticated.set(false);
      await this.router.navigate(['/']);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  }
}
